import React, { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { X, Delete, RefreshCw } from 'lucide-react'

interface Props {
  onClose?: () => void
}

export function CalculatorWidget({ onClose }: Props) {
  const [display, setDisplay] = useState('0')
  const [equation, setEquation] = useState('')
  const [isNewNumber, setIsNewNumber] = useState(true)

  const handleDigit = (digit: string) => {
    if (isNewNumber) {
      setDisplay(digit === '.' ? '0.' : digit)
      setIsNewNumber(false)
    } else {
      if (digit === '.' && display.includes('.')) return
      setDisplay(prev => prev + digit)
    }
  }

  const handleOp = (op: string) => {
    setEquation(`${display} ${op} `)
    setIsNewNumber(true)
  }

  const handleClear = () => {
    setDisplay('0')
    setEquation('')
    setIsNewNumber(true)
  }

  const handleBackspace = () => {
    if (isNewNumber) return
    setDisplay(prev => (prev.length > 1 ? prev.slice(0, -1) : '0'))
  }

  const handleEqual = () => {
    try {
      const fullExpr = `${equation}${display}`
        .replace(/×/g, '*')
        .replace(/÷/g, '/')
        .replace(/−/g, '-')
      
      // Safe math evaluate using standard math tokens
      // eslint-disable-next-line no-eval
      const res = Function(`'use strict'; return (${fullExpr})`)()
      setDisplay(String(Number(res.toFixed(8))))
      setEquation('')
      setIsNewNumber(true)
    } catch {
      setDisplay('Error')
      setIsNewNumber(true)
    }
  }

  const handleSpecial = (type: 'sqrt' | 'sq' | 'sign' | 'percent') => {
    const val = parseFloat(display)
    if (isNaN(val)) return

    switch (type) {
      case 'sqrt':
        setDisplay(String(Math.sqrt(Math.max(0, val))))
        break
      case 'sq':
        setDisplay(String(val * val))
        break
      case 'sign':
        setDisplay(String(-val))
        break
      case 'percent':
        setDisplay(String(val / 100))
        break
    }
    setIsNewNumber(true)
  }

  return (
    <Card className="w-72 bg-card/95 backdrop-blur-glass border-glass-border shadow-glass-md rounded-2xl overflow-hidden select-none z-50">
      <div className="flex items-center justify-between px-3.5 py-2.5 bg-muted/40 border-b border-border">
        <span className="text-xs font-bold text-foreground">Calculator</span>
        {onClose && (
          <Button variant="ghost" size="icon" className="h-6 w-6 rounded-md hover:bg-muted" onClick={onClose}>
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <CardContent className="p-3 space-y-3">
        {/* Display Screen */}
        <div className="bg-background/80 border border-border/80 rounded-xl p-2.5 text-right space-y-0.5">
          <div className="text-[10px] text-muted-foreground font-mono h-3.5 truncate">
            {equation}
          </div>
          <div className="text-xl font-bold font-mono text-foreground truncate">
            {display}
          </div>
        </div>

        {/* Keypad Grid */}
        <div className="grid grid-cols-4 gap-1.5 text-xs font-bold font-mono">
          <Button variant="outline" size="sm" onClick={handleClear} className="h-8 text-destructive hover:bg-destructive/10 border-destructive/20 rounded-lg">C</Button>
          <Button variant="outline" size="sm" onClick={() => handleSpecial('sqrt')} className="h-8 rounded-lg">√</Button>
          <Button variant="outline" size="sm" onClick={() => handleSpecial('percent')} className="h-8 rounded-lg">%</Button>
          <Button variant="outline" size="sm" onClick={() => handleOp('÷')} className="h-8 text-primary bg-primary/5 rounded-lg">÷</Button>

          <Button variant="outline" size="sm" onClick={() => handleDigit('7')} className="h-8 bg-card rounded-lg">7</Button>
          <Button variant="outline" size="sm" onClick={() => handleDigit('8')} className="h-8 bg-card rounded-lg">8</Button>
          <Button variant="outline" size="sm" onClick={() => handleDigit('9')} className="h-8 bg-card rounded-lg">9</Button>
          <Button variant="outline" size="sm" onClick={() => handleOp('×')} className="h-8 text-primary bg-primary/5 rounded-lg">×</Button>

          <Button variant="outline" size="sm" onClick={() => handleDigit('4')} className="h-8 bg-card rounded-lg">4</Button>
          <Button variant="outline" size="sm" onClick={() => handleDigit('5')} className="h-8 bg-card rounded-lg">5</Button>
          <Button variant="outline" size="sm" onClick={() => handleDigit('6')} className="h-8 bg-card rounded-lg">6</Button>
          <Button variant="outline" size="sm" onClick={() => handleOp('−')} className="h-8 text-primary bg-primary/5 rounded-lg">−</Button>

          <Button variant="outline" size="sm" onClick={() => handleDigit('1')} className="h-8 bg-card rounded-lg">1</Button>
          <Button variant="outline" size="sm" onClick={() => handleDigit('2')} className="h-8 bg-card rounded-lg">2</Button>
          <Button variant="outline" size="sm" onClick={() => handleDigit('3')} className="h-8 bg-card rounded-lg">3</Button>
          <Button variant="outline" size="sm" onClick={() => handleOp('+')} className="h-8 text-primary bg-primary/5 rounded-lg">+</Button>

          <Button variant="outline" size="sm" onClick={() => handleSpecial('sign')} className="h-8 rounded-lg">±</Button>
          <Button variant="outline" size="sm" onClick={() => handleDigit('0')} className="h-8 bg-card rounded-lg">0</Button>
          <Button variant="outline" size="sm" onClick={() => handleDigit('.')} className="h-8 bg-card rounded-lg">.</Button>
          <Button variant="default" size="sm" onClick={handleEqual} className="h-8 bg-primary text-primary-foreground rounded-lg">=</Button>
        </div>
      </CardContent>
    </Card>
  )
}
